import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Skeleton, 
  Table, 
  TableHead, 
  TableBody, 
  TableRow, 
  TableCell, 
  Chip,
  TextField,
  Grid,
  Button,
  Alert,
  InputAdornment
} from '@mui/material';
import { api } from '../../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { formatDate } from '../../utils/formatters';

interface KycRequest {
  requestId: number;
  userId: number;
  username: string;
  emailHash?: string;
  status: string;
  documentType?: string;
  documentUrl?: string;
  reviewerId?: number;
  reviewComment?: string;
  createdAt: string;
  updatedAt?: string;
}

interface KycRequestsResponse {
  data: KycRequest[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const fetchKycRequests = async (page = 1, limit = 50, search = ''): Promise<KycRequestsResponse> => {
  try {
    const params: any = { page, limit };
    if (search) params.search = search;

    const response = await api.get('/api/v1/admin-panel/kyc/requests', {
      params,
      headers: {
        'x-hasura-admin-secret': localStorage.getItem('hasura_admin_secret') || 'admin_secret_key'
      }
    });

    return response.data.data;
  } catch (error) {
    console.error('Error fetching KYC requests:', error);
    throw error;
  }
};

const KycRequestsPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requests, setRequests] = useState<KycRequest[]>([]);
  const [totalRequests, setTotalRequests] = useState(0);
  const [search, setSearch] = useState('');

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetchKycRequests(1, 50, search);
      
      if (response?.data && Array.isArray(response.data)) {
        setRequests(response.data);
        setTotalRequests(response.total || 0);
      } else {
        console.error('Invalid response structure:', response);
        setRequests([]);
        setTotalRequests(0);
        setError('Не удалось загрузить KYC запросы: неверный формат данных');
      }
    } catch (err: any) {
      console.error('Error loading KYC requests:', err);
      setRequests([]);
      setTotalRequests(0);
      setError(err.response?.data?.message || err.message || 'Ошибка при загрузке KYC запросов');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [search]);

  const handleRefresh = () => {
    loadRequests();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const renderRequestsTable = () => {
    if (loading) {
      return (
        <Box>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={60} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error">
          Ошибка при загрузке данных: {error}
        </Alert>
      );
    }

    if (!requests || requests.length === 0) {
      return (
        <Alert severity="info">
          KYC запросы не найдены
        </Alert>
      );
    }

    return (
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Пользователь</TableCell>
            <TableCell>Тип документа</TableCell>
            <TableCell>Статус</TableCell>
            <TableCell>Комментарий</TableCell>
            <TableCell>Дата создания</TableCell>
            <TableCell>Дата обновления</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((request) => (
            <TableRow key={request.requestId}>
              <TableCell>{request.requestId}</TableCell>
              <TableCell>
                <Box>
                  <Typography variant="body2">
                    {request.username} (ID: {request.userId})
                  </Typography>
                  {request.emailHash && (
                    <Typography variant="caption" color="text.secondary">
                      {request.emailHash}
                    </Typography>
                  )}
                </Box>
              </TableCell>
              <TableCell>{request.documentType || '—'}</TableCell>
              <TableCell>
                <Chip 
                  label={request.status} 
                  color={getStatusColor(request.status)} 
                  size="small" 
                />
              </TableCell>
              <TableCell>{request.reviewComment || '—'}</TableCell>
              <TableCell>{formatDate(request.createdAt)}</TableCell>
              <TableCell>
                {request.updatedAt ? formatDate(request.updatedAt) : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        KYC запросы
      </Typography>
      
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              placeholder="Поиск по имени пользователя, статусу или типу документа"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Button 
              variant="contained" 
              onClick={handleRefresh} 
              size="small" 
              startIcon={<RefreshIcon />}
              fullWidth
            >
              Обновить
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Найдено запросов: {totalRequests}
        </Typography>
        {renderRequestsTable()}
      </Paper>
    </Box>
  );
};

export default KycRequestsPage; 